import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef, Component, ElementRef, forwardRef, inject, Input,
  OnDestroy, TemplateRef, ViewChild, ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

export interface DropdownOption {
  label: string;
  value: any;
}

@Component({ standalone: false,
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true,
    },
  ],
})
export class CustomDropdownComponent implements ControlValueAccessor, OnDestroy {
  @Input() options: DropdownOption[] = [];
  @Input() invalid = false;
  @Input() placeholder = 'Select an Option';
  @Input() inputId = '';            // lets <label for="..."> point at the trigger
  @Input() maxPanelHeight = 260;    // px
  @Input() showFocus = true;                        // false = no blue border / ring on focus or open
  @Input() borderColor = 'rgba(0, 0, 0, 0.25)';
  @Input() textColor = 'rgba(0, 0, 0, 0.50)';
  @Input() variant: 'default' | 'form' = 'default'; // 'form' = match .form-control text inputs
  @ViewChild('trigger', { static: true }) triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('panel', { static: true }) panelTemplate!: TemplateRef<any>;

  value: any = '';
  isOpen = false;
  disabled = false;
  activeIndex = -1;
  openedUp = false;
  private overlayRef: OverlayRef | null = null;
  private openSubs: Subscription | null = null;
  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private overlay = inject(Overlay);

  constructor(
    private vcr: ViewContainerRef,
    private cdr: ChangeDetectorRef,
  ) {}

  get selectedLabel(): string {
    const match = this.options.find(o => o.value === this.value);
    return match ? match.label : '';
  }

  get activeBorder(): string {
    if (this.invalid) { return '#dc3545'; }
    return this.showFocus ? '#003CFF' : this.borderColor;
  }
  
  // ── ControlValueAccessor ──────────────────────────────
  writeValue(v: any): void {
    this.value = v ?? '';
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) { this.close(); }
    this.cdr.markForCheck();
  }

  // ── Open / close ──────────────────────────────────────
  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    if (this.disabled || this.overlayRef?.hasAttached()) { return; }

    const el = this.triggerRef.nativeElement;
    const positions: ConnectedPosition[] = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },     // no offsetY
      { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },  // no offsetY
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(el)
      .withPositions(positions)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: el.getBoundingClientRect().width,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.openSubs = new Subscription();
    this.openSubs.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
    this.openSubs.add(this.overlayRef.keydownEvents().subscribe(e => this.onOverlayKey(e)));
    this.openSubs.add(
      positionStrategy.positionChanges.subscribe(change => {
        this.openedUp = change.connectionPair.originY === 'top';
        this.cdr.detectChanges();
      })
    );

    const selectedIdx = this.options.findIndex(o => o.value === this.value);
    this.activeIndex = selectedIdx >= 0 ? selectedIdx : 0;

    this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.vcr));
    this.isOpen = true;
    this.scrollActiveIntoView();
  }

  
  close(): void {
    if (!this.isOpen) { return; }
    this.openSubs?.unsubscribe();
    this.openSubs = null;
    this.overlayRef?.dispose();   
    this.overlayRef = null;
    this.isOpen = false;
    this.openedUp = false;
    this.activeIndex = -1;
    this.onTouched();
    this.cdr.markForCheck();
  }

  select(option: DropdownOption): void {
    this.value = option.value;
    this.onChange(option.value);
    this.close();
  }

  // ── Keyboard ──────────────────────────────────────────
  onTriggerKeydown(e: KeyboardEvent): void {
    // Enter/Space already trigger click → toggle(); only arrows need handling
    if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      this.open();
    }
  }

  private onOverlayKey(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
      case 'Tab':
        this.close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (this.activeIndex >= 0) { this.select(this.options[this.activeIndex]); }
        break;
    }
  }

  private moveActive(step: number): void {
    const last = this.options.length - 1;
    if (last < 0) { return; }
    this.activeIndex = Math.min(last, Math.max(0, this.activeIndex + step));
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      this.overlayRef?.overlayElement
        .querySelector('.cd-item.active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  ngOnDestroy(): void {
    this.openSubs?.unsubscribe();
    this.overlayRef?.dispose();
  }
}